import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { EMap, EmbedReportState } from '@mode/reports/sandbox/contract';
import { ErrorReporter, Logger } from '@mode/shared/contract-common';
import { createScriptLoader, isPresent } from '@mode/shared/util-js';
import { combineLatest, filter, map, take } from 'rxjs';
import { importRenderFunc } from '~/lib/charts/chart-renderer';
import { EmbedDatasetsFacade } from '../domain/datasets/embed-datasets.facade';
import { EmbedLayoutFacade } from '../domain/layout/embed-layout.facade';

export async function loadCustomScripts(
  window: Window,
  load: () => PromiseLike<void>,
  datasets: EMap<EmbedReportState.EmbedDataset>,
  orderedDatasets: EmbedReportState.EmbedDataset[]
) {
  // The following is to conform to the V1 datasets API for custom scripts
  // Do not change without confirming with someone familiar with the V1 datasets API
  window.dataset = { ...orderedDatasets[0] };
  window.dataset.datasets = datasets;
  window.datasets = orderedDatasets;
  // Now that Highcharts is being loaded once a report is requested.
  // We have to manually add it to the global window object.
  window['Highcharts'] = await importRenderFunc();
  await load();
}

// Service that applies CustomJs to the embeded report Dom.
@Injectable({
  providedIn: 'root',
})
export class CustomJsService {
  private customScriptLoader$ = this.embedLayoutFacade.initialScripts$.pipe(
    filter((scripts) => isPresent(scripts)),
    map((scripts) => createScriptLoader(scripts, this.document.head))
  );

  // This service sets up the chain of events that would apply customjs,
  // But they won't be applied until a component subscribes to this observable.
  customScriptsLoading$ = combineLatest([
    this.customScriptLoader$,
    this.embedDatasetsFacade.legacyEmbedDatasets$,
    this.embedDatasetsFacade.legacyEmbedDatasetsForLocalQueriesOrderedByCreationDate$,
  ]).pipe(
    filter(
      ([scriptLoader, datasets, orderedDatasets]) =>
        isPresent(scriptLoader) && isPresent(datasets) && isPresent(orderedDatasets)
    ),
    take(1), // We should only ever apply the scripts once
    map(([scriptLoader, datasets, orderedDatasets]) => {
      this.logger.debug('Applying CustomJs', { orderedDatasets });
      // Execute customjs code outside of our Angular zone so we don't report errors
      // Alamode has it's own manual reporting mechanism
      return this.ngZone.runOutsideAngular(() =>
        loadCustomScripts(this.window, scriptLoader, datasets, orderedDatasets).catch((error) => {
          this.errorService.notify({ error });
        })
      );
    })
  );

  constructor(
    private embedDatasetsFacade: EmbedDatasetsFacade,
    private embedLayoutFacade: EmbedLayoutFacade,
    private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private logger: Logger,
    private errorService: ErrorReporter,
    private ngZone: NgZone
  ) {}
}
